import { useState, useEffect, useRef } from "react";
import { useContent } from "@/context/ContentContext";
import { Save, RefreshCw, Upload, Search } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ── Image compression (same as MediaManager) ─────────────────────────────────
function compressImage(file) {
  return new Promise((resolve) => {
    if (file.size < 400 * 1024) { resolve(file); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        const MAX = 1920;
        if (width > MAX || height > MAX) {
          if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
          else { width = Math.round(width * MAX / height); height = MAX; }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext("2d");
        const isPng = file.type === "image/png";
        if (!isPng) { ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, width, height); }
        ctx.drawImage(img, 0, 0, width, height);
        const type = isPng ? "image/png" : "image/jpeg";
        canvas.toBlob((blob) => {
          if (!blob) { resolve(file); return; }
          const ext = isPng ? ".png" : ".jpg";
          resolve(new File([blob], file.name.replace(/\.[^.]+$/, ext), { type }));
        }, type, isPng ? undefined : 0.85);
      };
      img.onerror = () => resolve(file);
      img.src = e.target.result;
    };
    reader.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });
}

async function uploadFile(file) {
  const compressed = await compressImage(file);
  const fd = new FormData();
  fd.append("file", compressed);
  const res = await axios.post(`${API}/media/upload`, fd, {
    withCredentials: true,
    headers: { "Content-Type": "multipart/form-data" },
  });
  const url = res.data.url.startsWith("http") ? res.data.url : `${BACKEND_URL}${res.data.url}`;
  return url;
}

// ── UI helpers ────────────────────────────────────────────────────────────────
function SectionCard({ title, children }) {
  return (
    <div className="bg-white rounded-xl border border-[#E2E8F0] p-6 mb-6">
      <h3 className="font-['Montserrat'] font-semibold text-[#0F172A] text-base mb-4 pb-3 border-b border-[#E2E8F0]">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", placeholder, textarea, hint, maxLen, children }) {
  const len = (value || "").length;
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="font-['Montserrat'] text-sm font-bold text-[#0F172A]">{label}</label>
        {maxLen && (
          <span className={`text-xs font-['Montserrat'] ${len > maxLen ? "text-red-400" : len >= maxLen * 0.8 ? "text-amber-500" : "text-[#94A3B8]"}`}>
            {len}/{maxLen}
          </span>
        )}
      </div>
      {hint && <p className="text-[#94A3B8] text-xs mb-1">{hint}</p>}
      {textarea ? (
        <textarea rows={3} name={name} value={value} onChange={onChange} placeholder={placeholder}
          className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2.5 text-sm text-[#0F172A] font-['Montserrat'] focus:outline-none focus:border-[#0072A4] transition-colors resize-none"
          data-testid={`field-${name}`} />
      ) : (
        <input type={type} name={name} value={value} onChange={onChange} placeholder={placeholder}
          className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2.5 text-sm text-[#0F172A] font-['Montserrat'] focus:outline-none focus:border-[#0072A4] transition-colors"
          data-testid={`field-${name}`} />
      )}
      {children}
    </div>
  );
}

function UploadButton({ onUpload, uploading, label, testId }) {
  const ref = useRef();
  return (
    <div>
      <button type="button" onClick={() => ref.current?.click()} disabled={uploading}
        data-testid={testId}
        className="flex items-center gap-2 bg-[#0072A4] text-white font-['Montserrat'] font-bold text-sm px-4 py-2 rounded-lg hover:bg-[#005A80] disabled:opacity-60 transition-all">
        <Upload size={14} /> {uploading ? "Subiendo..." : label}
      </button>
      <input ref={ref} type="file" accept="image/*" onChange={onUpload} className="hidden" />
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function ContentEditor() {
  const { content, updateSection, refreshContent } = useContent();
  const [activeTab, setActiveTab] = useState("hero");
  const [hero, setHero] = useState({});
  const [general, setGeneral] = useState({});
  const [contact, setContact] = useState({});
  const [seo, setSeo] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (content.hero) setHero({ ...content.hero });
    if (content.general) setGeneral({ ...content.general });
    if (content.contact) setContact({ ...content.contact });
    if (content.seo) setSeo({ ...content.seo });
  }, [content]);

  const handleHeroChange = (e) => setHero(p => ({ ...p, [e.target.name]: e.target.value }));
  const handleGeneralChange = (e) => setGeneral(p => ({ ...p, [e.target.name]: e.target.value }));
  const handleContactChange = (e) => setContact(p => ({ ...p, [e.target.name]: e.target.value }));
  const handleSeoChange = (e) => setSeo(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      if (activeTab === "hero") await updateSection("hero", hero);
      if (activeTab === "general") await updateSection("general", general);
      if (activeTab === "contact") await updateSection("contact", contact);
      if (activeTab === "seo") await updateSection("seo", seo);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      setGeneral(p => ({ ...p, logo_url: url }));
    } catch (err) { console.error(err); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const handleHeroBgUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      setHero(p => ({ ...p, background_image: url }));
    } catch (err) { console.error(err); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const handleOgImageUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      setSeo(p => ({ ...p, og_image: url }));
    } catch (err) { console.error(err); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const tabs = [
    { id: "hero", label: "Hero" },
    { id: "general", label: "General" },
    { id: "contact", label: "Contacto" },
    { id: "seo", label: "SEO" },
  ];

  return (
    <div data-testid="content-editor">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-['Montserrat'] font-bold text-2xl text-[#0F172A] mb-1">Contenido de la Landing</h1>
          <p className="text-[#475569] font-['Montserrat'] text-sm">Edita textos, imágenes y posicionamiento SEO</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => refreshContent()}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[#E2E8F0] text-[#475569] font-['Montserrat'] text-sm hover:bg-[#F8FAFC] transition-colors"
            data-testid="refresh-content-btn">
            <RefreshCw size={15} />
          </button>
          <button onClick={handleSave} disabled={saving} data-testid="save-content-btn"
            className={`flex items-center gap-2 px-5 py-2 rounded-lg font-['Montserrat'] font-semibold text-sm transition-all ${saved ? "bg-green-500 text-white" : "bg-[#0072A4] text-white hover:bg-[#005A80]"} disabled:opacity-60`}>
            <Save size={15} />
            {saving ? "Guardando..." : saved ? "¡Guardado!" : "Guardar"}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-[#E2E8F0] overflow-x-auto">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`tab-${tab.id}`}
            className={`px-5 py-2.5 font-['Montserrat'] text-sm font-bold transition-colors -mb-px border-b-2 whitespace-nowrap flex items-center gap-1.5 ${activeTab === tab.id ? "border-[#0072A4] text-[#0072A4]" : "border-transparent text-[#475569] hover:text-[#0F172A]"}`}>
            {tab.id === "seo" && <Search size={13} />}
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── HERO TAB ──────────────────────────────────────────────────────── */}
      {activeTab === "hero" && (
        <div className="space-y-4">
          <SectionCard title="Textos del Hero">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Título principal" name="title" value={hero.title || ""} onChange={handleHeroChange} placeholder="Campus Verano Activo" />
              <Field label="Subtítulo (badge amarillo)" name="subtitle" value={hero.subtitle || ""} onChange={handleHeroChange} placeholder="La Estanca 2026" />
              <div className="sm:col-span-2"><Field label="Tagline" name="tagline" value={hero.tagline || ""} onChange={handleHeroChange} /></div>
              <div className="sm:col-span-2"><Field label="Descripción" name="description" value={hero.description || ""} onChange={handleHeroChange} textarea /></div>
              <Field label="Texto botón principal" name="cta_text" value={hero.cta_text || ""} onChange={handleHeroChange} placeholder="¡Inscríbete Ahora!" />
              <Field label="Texto botón naranja" name="cta_whatsapp_text" value={hero.cta_whatsapp_text || ""} onChange={handleHeroChange} placeholder="Saber más" />
            </div>
          </SectionCard>
          <SectionCard title="Imagen de fondo">
            <div className="space-y-3">
              <Field label="URL de imagen de fondo" name="background_image" value={hero.background_image || ""} onChange={handleHeroChange} placeholder="https://..." hint="Introduce una URL o sube una imagen" />
              <UploadButton onUpload={handleHeroBgUpload} uploading={uploading} label="Subir imagen de fondo" testId="hero-bg-upload" />
              {hero.background_image && (
                <img src={hero.background_image} alt="Preview fondo" className="w-full h-40 object-cover rounded-xl mt-2 border border-[#E2E8F0]" onError={e => e.target.style.display = "none"} />
              )}
            </div>
          </SectionCard>
          <SectionCard title="Burbujas de información (estadísticas)">
            <p className="text-[#94A3B8] font-['Montserrat'] text-xs mb-4">Estos valores aparecen en las 5 burbujas del fondo del Hero. Deja vacío para usar el valor por defecto.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Edades (valor)" name="stat_edades" value={hero.stat_edades || ""} onChange={handleHeroChange} placeholder="2013–2020" />
              <Field label="Edades (subtexto)" name="stat_edades_sub" value={hero.stat_edades_sub || ""} onChange={handleHeroChange} placeholder="6 a 13 años" />
              <Field label="Semana 1 (fechas)" name="stat_semana1" value={hero.stat_semana1 || ""} onChange={handleHeroChange} placeholder="22–26 Jun" />
              <Field label="Semana 2 (fechas)" name="stat_semana2" value={hero.stat_semana2 || ""} onChange={handleHeroChange} placeholder="29 Jun–3 Jul" />
              <Field label="Horario base" name="stat_horario" value={hero.stat_horario || ""} onChange={handleHeroChange} placeholder="9:00 – 14:00h" />
              <Field label="Texto de plazas" name="stat_plazas" value={hero.stat_plazas || ""} onChange={handleHeroChange} placeholder="Plazas limitadas" />
            </div>
          </SectionCard>
        </div>
      )}

      {/* ── GENERAL TAB ───────────────────────────────────────────────────── */}
      {activeTab === "general" && (
        <div className="space-y-4">
          <SectionCard title="Información del sitio">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><Field label="Título del sitio" name="site_title" value={general.site_title || ""} onChange={handleGeneralChange} /></div>
              <Field label="Organización" name="organization" value={general.organization || ""} onChange={handleGeneralChange} />
              <Field label="Director" name="director" value={general.director || ""} onChange={handleGeneralChange} />
              <Field label="WhatsApp (sin +34)" name="whatsapp" value={general.whatsapp || ""} onChange={handleGeneralChange} placeholder="657871824" />
              <Field label="Email" name="email" value={general.email || ""} onChange={handleGeneralChange} type="email" />
            </div>
          </SectionCard>

          <SectionCard title="Logo del campus">
            <div className="flex flex-col sm:flex-row gap-6 items-start">
              {/* Preview circular */}
              <div className="flex-shrink-0">
                {general.logo_url ? (
                  <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-[#E2E8F0] shadow"
                    data-testid="logo-preview"
                    style={{ background: "#fff" }}>
                    <img src={general.logo_url} alt="Logo preview" className="w-full h-full object-contain p-1"
                      onError={e => e.target.style.display = "none"} />
                  </div>
                ) : (
                  <div className="w-24 h-24 rounded-full bg-[#F8FAFC] border-4 border-dashed border-[#E2E8F0] flex items-center justify-center">
                    <Upload size={24} color="#CBD5E1" />
                  </div>
                )}
                <p className="text-xs text-[#94A3B8] font-['Montserrat'] text-center mt-2">Vista previa circular</p>
              </div>

              {/* Fields */}
              <div className="flex-1 space-y-3 w-full">
                <Field label="URL del logo" name="logo_url" value={general.logo_url || ""} onChange={handleGeneralChange}
                  placeholder="https://..." hint="Pega una URL o sube el archivo directamente" />
                <UploadButton onUpload={handleLogoUpload} uploading={uploading} label="Subir logo" testId="logo-upload" />
                {uploading && <p className="text-xs text-[#94A3B8] font-['Montserrat']">Comprimiendo y subiendo...</p>}
              </div>
            </div>
          </SectionCard>
        </div>
      )}

      {/* ── CONTACT TAB ───────────────────────────────────────────────────── */}
      {activeTab === "contact" && (
        <SectionCard title="Información de Contacto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="WhatsApp" name="whatsapp" value={contact.whatsapp || ""} onChange={handleContactChange} placeholder="657871824" />
            <Field label="Email" name="email" value={contact.email || ""} onChange={handleContactChange} type="email" />
            <Field label="Director" name="director" value={contact.director || ""} onChange={handleContactChange} />
            <Field label="Organización" name="organization" value={contact.organization || ""} onChange={handleContactChange} />
            <div className="sm:col-span-2"><Field label="Localización / Lugar" name="location" value={contact.location || ""} onChange={handleContactChange} placeholder="La Estanca" /></div>
          </div>
        </SectionCard>
      )}

      {/* ── SEO TAB ───────────────────────────────────────────────────────── */}
      {activeTab === "seo" && (
        <div className="space-y-4">
          {/* Guide */}
          <div className="flex items-start gap-3 bg-[#F0F8FF] border border-[#0072A4]/20 rounded-xl p-4">
            <Search size={18} color="#0072A4" className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-['Montserrat'] font-bold text-[#0072A4] text-sm">Posicionamiento en buscadores y chats de IA</p>
              <p className="font-['Montserrat'] text-[#475569] text-xs mt-0.5">
                Estos campos controlan cómo aparece el campus en Google, Bing, ChatGPT, Perplexity y redes sociales.
                Se generan automáticamente los datos estructurados (JSON-LD) para resultados enriquecidos.
              </p>
            </div>
          </div>

          <SectionCard title="Meta básico">
            <div className="space-y-4">
              <Field label="Meta título" name="meta_title" value={seo.meta_title || ""} onChange={handleSeoChange}
                maxLen={60} placeholder="Campus Verano Activo La Estanca 2026 | Campamento para Niños"
                hint="Ideal: 50–60 caracteres. Aparece en la pestaña del navegador y en Google." />
              <Field label="Meta descripción" name="meta_description" value={seo.meta_description || ""} onChange={handleSeoChange}
                textarea maxLen={160} placeholder="Campamento de verano en La Estanca 2026..."
                hint="Ideal: 120–160 caracteres. Aparece en el snippet de Google y en chats de IA." />
              <Field label="Keywords (palabras clave)" name="meta_keywords" value={seo.meta_keywords || ""} onChange={handleSeoChange}
                textarea hint="Separadas por comas. Usadas por algunos buscadores y para contexto en IA." placeholder="campamento verano niños, campus verano La Estanca..." />
              <Field label="URL canónica" name="canonical_url" value={seo.canonical_url || ""} onChange={handleSeoChange}
                placeholder="https://tudominio.com" hint="URL definitiva del sitio (sin /admin). Evita contenido duplicado." />
            </div>
          </SectionCard>

          <SectionCard title="Open Graph y redes sociales">
            <div className="space-y-4">
              <Field label="Nombre del sitio" name="site_name" value={seo.site_name || ""} onChange={handleSeoChange}
                placeholder="Campus Verano Activo La Estanca 2026" />
              <Field label="Idioma (locale)" name="og_locale" value={seo.og_locale || "es_ES"} onChange={handleSeoChange}
                hint="Ej: es_ES (España), es_AR (Argentina)" />
              <Field label="Twitter/X handle" name="twitter_handle" value={seo.twitter_handle || ""} onChange={handleSeoChange}
                placeholder="@MiCuenta" hint="Opcional. Ej: @CampusVeranoActivo" />

              {/* OG Image */}
              <div className="space-y-2">
                <Field label="Imagen para redes sociales (OG Image)" name="og_image" value={seo.og_image || ""} onChange={handleSeoChange}
                  placeholder="https://..." hint="Recomendado: 1200×630px. Aparece al compartir en WhatsApp, Facebook, Twitter y LinkedIn." />
                <UploadButton onUpload={handleOgImageUpload} uploading={uploading} label="Subir imagen OG" testId="og-image-upload" />
                {seo.og_image && (
                  <img src={seo.og_image} alt="OG preview" className="w-full max-w-sm h-32 object-cover rounded-xl border border-[#E2E8F0] mt-2"
                    onError={e => e.target.style.display = "none"} />
                )}
              </div>
            </div>
          </SectionCard>

          {/* Google SERP Preview */}
          <SectionCard title="Vista previa en Google">
            <div className="rounded-xl border border-[#E2E8F0] p-5 bg-white max-w-xl">
              <p className="font-['Montserrat'] text-xs text-[#94A3B8] mb-3 uppercase tracking-wide">Así aparece en resultados de búsqueda</p>
              <p className="font-['Montserrat'] text-xs text-[#3C4043] truncate">{seo.canonical_url || "https://tusitio.com"}</p>
              <p className="font-['Montserrat'] text-[#1a0dab] text-lg font-medium mt-0.5 line-clamp-1 leading-tight"
                data-testid="serp-preview-title">
                {seo.meta_title || "Título de la página | Sitio web"}
              </p>
              <p className="font-['Montserrat'] text-[#4D5156] text-sm mt-1 line-clamp-2 leading-relaxed">
                {seo.meta_description || "Aquí aparecerá la descripción de tu página. Escribe algo que anime a hacer clic."}
              </p>
            </div>
          </SectionCard>

          {/* WhatsApp/Facebook Preview */}
          <SectionCard title="Vista previa al compartir en WhatsApp / redes">
            <div className="rounded-xl overflow-hidden border border-[#E2E8F0] max-w-sm bg-white shadow-sm">
              {seo.og_image ? (
                <img src={seo.og_image} alt="OG" className="w-full h-40 object-cover" onError={e => e.target.style.display = "none"} />
              ) : (
                <div className="w-full h-40 bg-[#F0F8FF] flex items-center justify-center">
                  <p className="font-['Montserrat'] text-[#94A3B8] text-xs">Sin imagen OG</p>
                </div>
              )}
              <div className="p-3 border-t border-[#E2E8F0]">
                <p className="font-['Montserrat'] text-xs text-[#94A3B8] uppercase tracking-wide">{(() => { try { return new URL(seo.canonical_url?.startsWith("http") ? seo.canonical_url : `https://${seo.canonical_url || "tusitio.com"}`).hostname; } catch { return "tusitio.com"; } })()}</p>
                <p className="font-['Montserrat'] font-bold text-[#0F172A] text-sm mt-0.5 line-clamp-2">{seo.meta_title || "Título del sitio"}</p>
                <p className="font-['Montserrat'] text-[#475569] text-xs mt-0.5 line-clamp-2">{seo.meta_description || "Descripción..."}</p>
              </div>
            </div>
          </SectionCard>

          {/* Structured data info */}
          <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-5">
            <p className="font-['Montserrat'] font-bold text-[#0F172A] text-sm mb-2">Datos estructurados generados automáticamente</p>
            <div className="space-y-1.5">
              {[
                { emoji: "📅", text: "Event (Google Events): Semanas 22 Jun–3 Jul 2026" },
                { emoji: "❓", text: "FAQPage: Preguntas frecuentes activas del admin" },
                { emoji: "🏢", text: "Organization: Asociación MuéveTe + contacto" },
              ].map(({ emoji, text }) => (
                <div key={text} className="flex items-center gap-2">
                  <span>{emoji}</span>
                  <p className="font-['Montserrat'] text-[#475569] text-xs">{text}</p>
                </div>
              ))}
            </div>
            <p className="font-['Montserrat'] text-[#94A3B8] text-xs mt-3">
              Estos schemas JSON-LD están embebidos en el HTML y son leídos por Google, Bing, ChatGPT y otros buscadores para generar resultados enriquecidos.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
